import { AppBar, Avatar, Box, Button, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { getIsAuthorized, getGuideStep, setGuideStep } from '../reducers/main/mainReducer';
import { useDispatch, useSelector } from 'react-redux';
import { Palette, Sizes } from '../@shared/palette';
import LogoutIcon from '@mui/icons-material/Logout';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { FlexBoxInline, LinkSxSmall, PrimaryBtnSx, SecondaryBtnSx, ThirdBtnSx, TooltipSlotProps } from '../@shared/templates';
import { UserAvatar } from './UserAvatar';
import { LogoWithTextSmall } from './icons/LogoWithTextSmall';
import moment from 'moment';
import TryIcon from '@mui/icons-material/Try';
import GuideCallout from './GuideCallout';
import { GuideStepEnum } from '../reducers/main/mainModel';
import { useLocation, useNavigate } from 'react-router-dom';

export const AppBarComponent = () => {
    const [userEl, setUserEl] = React.useState<null | HTMLElement>(null);
    const [showInitialGuideCallout, setShowInitialGuideCallout] = React.useState(() => !localStorage.getItem("hideGuideCallout"));
    const open = Boolean(userEl);
    const user = useSelector(getIsAuthorized)
    const navigate = useNavigate();
    const location = useLocation();
    const breadcrumbsArray = React.useMemo(() => location.pathname.split("/"), [location.pathname]);
    const guideStep = useSelector(getGuideStep);
    const dispatch = useDispatch();
    const openHomePage = React.useCallback(() => {
        navigate('/');
    }, [navigate]);

    const stringToColor = React.useCallback((string: string) => {
        let hash = 0;
        let i;

        for (let i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = '#';

        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }

        return color;
    }, [])

    const stringAvatar = React.useCallback((size: number) => {
        const name = user?.name ?? user?.email;
        const attrSX = {
            height: size + "px",
            width: size + "px",
            fontSize: size > 50 ? Sizes.textLogo : Sizes.textPrimary,
        }

        if (!name) {
            return {
                sx: attrSX
            };
        }

        const splitedName = name.split(' ');

        return {
            sx: {
                bgcolor: stringToColor(name),
                ...attrSX
            },
            children: splitedName.length > 1 ? `${name.split(' ')[0][0]}${name.split(' ')[1][0]}` : name.split(' ')[0][0],
        };
    }, [stringToColor, user?.email, user?.name]);

    const onCloseUserDetails = () => {
        setUserEl(null);
    };

    const onLogout = React.useCallback(() => {
        localStorage.removeItem("token");
        navigate('/login');
    }, [navigate]);

    const onOpenDocs = React.useCallback(() => {
        onCloseUserDetails();
        navigate('/Documentation');
    }, [navigate]);

    const onOpenSettings = React.useCallback(() => {
        onCloseUserDetails();
        navigate('/Settings');
    }, [navigate]);
    const onOpenSubscription = React.useCallback(() => {
        onCloseUserDetails();
        navigate('/Subscription');
    }, [navigate]);

    const getLicenseStatus = React.useCallback(() => {
        if (!user) {
            return "Not authorized";
        }

        if (user.license) {
            const utcMoment = moment().utc();
            const licenseEnd = moment(user.license.expirationDate);
            if (licenseEnd.isBefore(utcMoment)) {
                return `Expired (${user.license.licenseType})`;
            }

            return `${user.license.licenseType}`;
        }

        return "No license";
    }, [user]);

    return <AppBar position='relative' sx={{
        background: Palette.backgroundColorCard,
        borderBottom: Palette.borderPrimary,
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        height: AppBarHeight,
        boxShadow: "none",
        color: Palette.textColorPrimary,
    }}>
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px"
        }}>
            <Button sx={{ padding: "10px", paddingLeft: "5px", paddingRight: "15px", color: Palette.textColorPrimary, textTransform: "none", fontSize: Sizes.textLogo }} onClick={openHomePage}>
                <LogoWithTextSmall />
            </Button>
            <Box sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "5px"
            }}>
                {breadcrumbsArray.map((x, i) => {
                    return x && <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "5px",
                            marginRight: "5px"
                        }}
                        key={i}>
                        <Typography>/</Typography>
                        <Button
                            onClick={() => navigate(`/${breadcrumbsArray.slice(1, i + 1).join('/')}`)}
                            sx={ThirdBtnSx}>{x}</Button>
                    </Box>
                })
                }
            </Box>
        </Box>

        <Box sx={{ ...FlexBoxInline, gap: "10px", padding: "15px" }} >
            <Tooltip placement="left-start" open={showInitialGuideCallout} slotProps={TooltipSlotProps} title={
                <GuideCallout onClose={() => setShowInitialGuideCallout(false)}>
                    Click to enable step by step guide on how to use the platform.
                    <br />
                    <Button
                        sx={{ ...LinkSxSmall, padding: 0 }}
                        onClick={() => {
                            localStorage.setItem("hideGuideCallout", "true");
                            setShowInitialGuideCallout(false);
                        }}>
                        Do not show again
                    </Button>
                </GuideCallout>
            } arrow>
                <IconButton
                    sx={{ ...(guideStep !== GuideStepEnum.None ? PrimaryBtnSx : SecondaryBtnSx), border: "none", p: "8px" }}
                    onClick={() => {
                        dispatch(setGuideStep(guideStep !== GuideStepEnum.None ? GuideStepEnum.None : GuideStepEnum.ClickSchemaBtn));
                        setShowInitialGuideCallout(false);
                    }}>
                    <TryIcon sx={{ fontSize: "1.3rem" }} />
                </IconButton>
            </Tooltip>
            <UserAvatar user={user} onClick={(e) => setUserEl(e.currentTarget)} />
        </Box>
        <Menu
            id="basic-menu"
            anchorEl={userEl}
            open={open}
            onClose={onCloseUserDetails}
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: Palette.backgroundColorPrimary,
                }
            }}
        >
            <Box sx={{
                maxWidth: "300px",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
            }}>
                <Box sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                    padding: "15px"
                }}>
                    <Avatar {...stringAvatar(64)} alt={user?.name} src={user?.imageUrl} />
                    <Box>
                        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>{user?.name}</Typography>
                        <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary }}>{user?.email}</Typography>
                        <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary }}>License: {getLicenseStatus()}</Typography>
                    </Box>
                </Box>
                <Divider orientation="horizontal" flexItem />
                <Box>
                    <MenuItem onClick={onOpenSubscription}>
                        <ListItemIcon>
                            <AutoAwesomeIcon sx={{ color: Palette.textColorPrimary }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Subscription</Typography>
                        </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={onOpenSettings}>
                        <ListItemIcon>
                            <SettingsIcon sx={{ color: Palette.textColorPrimary }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Settings</Typography>
                        </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={onOpenDocs}>
                        <ListItemIcon>
                            <ArticleIcon sx={{ color: Palette.textColorPrimary }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Documentation</Typography>
                        </ListItemText>
                    </MenuItem>
                </Box>
                <Divider orientation="horizontal" flexItem />
                <Box>
                    <MenuItem onClick={onLogout}>
                        <ListItemIcon>
                            <LogoutIcon sx={{ color: Palette.textColorPrimary }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Log out</Typography>
                        </ListItemText>
                    </MenuItem>
                </Box>
            </Box>
        </Menu>
    </AppBar>
}


export const AppBarHeight = 50;

